import React, { useEffect, useState, useCallback } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import lpApi from '../../helpers/lpApi';
import s from './LpPayment.css';

// =============================================================================
// LARGE PROJECTS — ON-SESSION client-paid DEPOSIT (down-payment) checkout (web
// parity with the app's project_deposit_view.dart). The client is PRESENT and
// pays the deposit NOW with Card OR a BNPL/wallet method (Affirm / Klarna /
// Afterpay / Cash App — admin-gated via lpPaymentMethods, OFF by default).
//
//   lpPaymentMethods                  -> which methods to show (card always on)
//   createDepositPaymentIntent(id)    -> { clientSecret, paymentMethodTypes,
//                                          depositAmount, billing* } (NO confirm)
//   stripe.confirm*Payment(...)       -> card = inline; BNPL = redirect (return_url)
//   confirmDepositPaid(id) [polled]   -> contract ACTIVE (escrow holds deposit)
//
// Reuses the app-wide Stripe.js loader (Elements in App.js) via useStripe(). The
// off-session held-card path (milestones) is card-only and lives elsewhere —
// this is the on-session deposit only. SEPARATE from the small-jobs checkout.
// =============================================================================

const Q_METHODS = `query { lpPaymentMethods { status lpPaymentMethodsJson } }`;
const M_CREATE = `
  mutation ($contractId: String!) {
    createDepositPaymentIntent(contractId: $contractId) {
      status errorMessage clientSecret paymentMethodTypes depositAmount
      billingLine1 billingCity billingState billingPostalCode billingCountry
    }
  }
`;
const M_CONFIRM = `
  mutation ($contractId: String!) {
    confirmDepositPaid(contractId: $contractId) {
      status errorMessage contract { id status depositPaidAt }
    }
  }
`;

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ICONS = {
  card: 'M3 10h18M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z',
  clock: 'M12 7v5l3 2 M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
  cal: 'M7 3v4M17 3v4M3 9h18M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z',
  split: 'M8 3v18M16 3v18M3 12h18',
  cash: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  lock: 'M6 10V8a6 6 0 1 1 12 0v2 M5 10h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z',
  check: 'M20 6 9 17l-5-5',
  radioOn: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  radioOff: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
};

const METHOD_META = {
  card: { label: 'Card', sub: 'Visa · Mastercard · Amex', icon: ICONS.card },
  affirm: { label: 'Affirm', sub: 'Pay over time', icon: ICONS.clock },
  klarna: { label: 'Klarna', sub: 'Pay in installments', icon: ICONS.cal },
  afterpay_clearpay: { label: 'Afterpay', sub: 'Pay in 4 · interest-free', icon: ICONS.split },
  cashapp: { label: 'Cash App Pay', sub: 'Pay from your Cash App', icon: ICONS.cash },
};
const BNPL_ORDER = ['affirm', 'klarna', 'afterpay_clearpay', 'cashapp'];

const elementOptions = {
  style: {
    base: { color: '#0f1214', fontSize: '15px', fontFamily: 'inherit', '::placeholder': { color: '#9aa0a6' } },
    invalid: { color: '#b42318' },
  },
};

function money(n, currency) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const cur = currency || 'USD';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${cur && cur !== 'USD' ? ` ${cur}` : ''}`;
}

function LpDepositCheckout(props) {
  const { contractId, downPaymentAmount, currency } = props;
  const stripe = useStripe();
  const elements = useElements();

  const [methods, setMethods] = useState({ card: true });
  const [selected, setSelected] = useState('card');
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Load enabled methods (card always on; BNPL admin-gated).
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await lpApi(Q_METHODS);
        const env = res && res.data && res.data.lpPaymentMethods;
        if (!mounted || !env || env.status !== 200) return;
        let m = {};
        try { m = JSON.parse(env.lpPaymentMethodsJson || '{}'); } catch (e) { m = {}; }
        m.card = m.card !== false;
        setMethods(m);
        if (selected !== 'card' && m[selected] !== true) setSelected('card');
      } catch (e) { /* fail-safe: card only */ }
    })();
    return () => { mounted = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll confirmDepositPaid: 200 = ACTIVE, 202 = received/async, 409 = retry.
  const pollConfirm = useCallback(async () => {
    for (let i = 0; i < 8; i += 1) {
      try {
        const r = await lpApi(M_CONFIRM, { contractId: String(contractId) });
        const env = r && r.data && r.data.confirmDepositPaid;
        if (env) {
          if (env.status === 200) return 'active';
          if (env.status === 202) return 'pending';
        }
      } catch (e) { /* retry */ }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 3000));
    }
    return 'pending';
  }, [contractId]);

  // BNPL redirect return: if we came back with ?lpDepositReturn=<id> for THIS
  // contract, resume polling (the redirect settled the PaymentIntent).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('lpDepositReturn') !== String(contractId)) return;
    setBusy(true);
    setNotice('Confirming your payment…');
    (async () => {
      const result = await pollConfirm();
      setBusy(false);
      if (result === 'active') {
        setPaid(true);
        setNotice('Deposit paid — your contract is now active.');
        if (props.onPaid) props.onPaid();
      } else {
        setPaid(true);
        setNotice('Deposit received. We are finalizing your payment and your contract will activate shortly.');
        if (props.onPaid) props.onPaid();
      }
    })();
  }, [contractId, pollConfirm]); // eslint-disable-line react-hooks/exhaustive-deps

  const billingDetails = (created) => ({
    name: props.clientName || undefined,
    email: props.clientEmail || undefined,
    address: {
      line1: created.billingLine1 || undefined,
      city: created.billingCity || undefined,
      state: created.billingState || undefined,
      postal_code: created.billingPostalCode || undefined,
      country: created.billingCountry || 'US',
    },
  });

  const returnUrl = () => {
    if (typeof window === 'undefined') return undefined;
    const base = window.location.origin + window.location.pathname;
    return `${base}?lpDepositReturn=${encodeURIComponent(String(contractId))}`;
  };

  const pay = async () => {
    if (busy) return;
    setError('');
    setNotice('');
    if (!stripe || !elements) {
      setError('Payment form is still loading — please try again.');
      return;
    }
    setBusy(true);
    try {
      // 1) Build the on-session PaymentIntent (server does NOT confirm).
      const cr = await lpApi(M_CREATE, { contractId: String(contractId) });
      const created = cr && cr.data && cr.data.createDepositPaymentIntent;
      if (cr.errors || !created || created.status !== 200 || !created.clientSecret) {
        setBusy(false);
        setError((created && created.errorMessage)
          || (cr.errors && cr.errors[0] && cr.errors[0].message)
          || 'Could not start the deposit payment.');
        return;
      }
      const clientSecret = created.clientSecret;

      // 2) Confirm with the selected method.
      let confirm;
      if (selected === 'card') {
        const cardNumber = elements.getElement(CardNumberElement);
        if (!cardNumber) { setBusy(false); setError('Please enter your card details.'); return; }
        confirm = await stripe.confirmCardPayment(clientSecret, {
          payment_method: { card: cardNumber, billing_details: billingDetails(created) },
        });
      } else if (selected === 'affirm') {
        confirm = await stripe.confirmAffirmPayment(clientSecret, {
          payment_method: { billing_details: billingDetails(created) }, return_url: returnUrl(),
        });
      } else if (selected === 'klarna') {
        confirm = await stripe.confirmKlarnaPayment(clientSecret, {
          payment_method: { billing_details: billingDetails(created) }, return_url: returnUrl(),
        });
      } else if (selected === 'afterpay_clearpay') {
        confirm = await stripe.confirmAfterpayClearpayPayment(clientSecret, {
          payment_method: { billing_details: billingDetails(created) }, return_url: returnUrl(),
        });
      } else if (selected === 'cashapp') {
        confirm = await stripe.confirmCashappPayment(clientSecret, {
          payment_method: { billing_details: billingDetails(created) }, return_url: returnUrl(),
        });
      }

      // BNPL/wallet redirect: the browser navigates away; on return the
      // lpDepositReturn effect resumes polling. If we reach here without a
      // redirect, fall through to confirm handling below.
      if (confirm && confirm.error) {
        setBusy(false);
        setError(confirm.error.message || 'Payment was not completed.');
        return;
      }

      // 3) Finalize (card path, or BNPL that resolved without redirect).
      setNotice('Confirming your deposit…');
      const result = await pollConfirm();
      setBusy(false);
      if (result === 'active') {
        setPaid(true);
        setNotice('Deposit paid — your contract is now active.');
        if (props.onPaid) props.onPaid();
      } else {
        setPaid(true);
        setNotice('Deposit received. We are finalizing your payment and your contract will activate shortly.');
        if (props.onPaid) props.onPaid();
      }
    } catch (e) {
      setBusy(false);
      setError(e.message || 'Payment was not completed. Please try again.');
    }
  };

  const enabled = ['card', ...BNPL_ORDER.filter((m) => methods[m] === true)];
  const gross = downPaymentAmount != null ? Number(downPaymentAmount) : null;

  if (paid) {
    return (
      <div className={s.wrap}>
        <div className={s.card}>
          <div className={s.successWrap}>
            <div className={s.successIcon}><Icon d={ICONS.check} /></div>
            <p className={s.successTitle}>Deposit paid</p>
            <p className={s.successBody}>{notice || 'Your contract is now active.'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.wrap}>
      <div className={s.card}>
        <p className={s.cardTitle}><Icon d={ICONS.lock} /> Pay your deposit</p>
        <p className={s.cardSub}>
          Pay the down payment now to activate your contract. It is held by BestHand in escrow and
          released to the company milestone by milestone — only after you approve the completed work.
        </p>

        {gross != null && (
          <div className={s.amountRow}>
            <span className={s.amountLabel}>Deposit due now</span>
            <span className={s.amountVal}>{money(gross, currency)}</span>
          </div>
        )}

        {error ? <div className={s.alertErr}>{error}</div> : null}
        {notice ? <div className={s.alertInfo}>{notice}</div> : null}

        {/* method selector — card always; BNPL when admin-enabled */}
        {enabled.map((key) => {
          const meta = METHOD_META[key] || { label: key, sub: '', icon: ICONS.card };
          const on = selected === key;
          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div key={key} className={`${s.method} ${on ? s.methodOn : ''}`} onClick={() => setSelected(key)}>
              <span className={s.methodIcon}><Icon d={meta.icon} /></span>
              <div className={s.methodMain}>
                <div className={s.methodName}>{meta.label}</div>
                <div className={s.methodSub}>{meta.sub}</div>
              </div>
              <span className={s.radio}><Icon d={on ? ICONS.radioOn : ICONS.radioOff} /></span>
            </div>
          );
        })}

        {selected === 'card' ? (
          <div style={{ marginTop: 4 }}>
            <div className={s.fieldGroup}>
              <div>
                <span className={s.fieldLabel}>Card number</span>
                <div className={s.fieldBox}><CardNumberElement options={elementOptions} /></div>
              </div>
              <div className={s.fieldRow2}>
                <div>
                  <span className={s.fieldLabel}>Expiry</span>
                  <div className={s.fieldBox}><CardExpiryElement options={elementOptions} /></div>
                </div>
                <div>
                  <span className={s.fieldLabel}>CVC</span>
                  <div className={s.fieldBox}><CardCvcElement options={elementOptions} /></div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={s.alertInfo}>
            You will approve this payment in a secure {(METHOD_META[selected] || {}).label || selected} checkout.
          </div>
        )}

        <button type="button" className={s.btn} disabled={busy} onClick={pay}>
          <Icon d={ICONS.lock} /> {busy ? 'Processing…' : `Pay deposit${gross != null ? ` ${money(gross, currency)}` : ''}`}
        </button>
        <p className={s.note}>
          Payments are processed securely by Stripe. Your deposit is protected by BestHand escrow.
        </p>
      </div>
    </div>
  );
}

export default withStyles(s)(LpDepositCheckout);
